import {
  ConfigObjectType,
  getRouterLocationQuery,
  navigationContainer,
  StateId,
} from '@wallaby-ui/core';
import {debounce} from 'lodash';
import * as React from 'react';
import {useMemo} from 'react';
import {connect, ConnectedProps} from 'react-redux';
import {getSearchServiceUrl} from 'src/js/containers/filter_and_search/filter_and_search_selectors';
import {changeParamsAndQuery} from 'src/js/containers/filter_and_search/filter_and_search_thunks';
import {
  Filter,
  FILTER_TYPE_BOOL,
  FILTER_TYPE_RADIO,
} from 'src/js/lib/filtering';
import {
  AutocompleteMethod,
  FilterSchema,
  FilterNode as FilterNodeModel,
} from 'src/js/model/filters_config';
import {RootState} from 'src/js/store';

import {MuiFilterNode} from './MuiFilterNode';
import {
  autocomplete,
  autocompleteGetLabels,
  autocompleteSetValues,
  changeFilter,
  changeFilterAndQuery,
  focusFilter,
  removeFilter,
  removeFilterAndQuery,
  toggleOpened,
} from '../filtering_actions';
import {
  makeGetSelectedAutocompleteValues,
  makeGetSelectedFilter,
  makeGetOpenedFilter,
  makeIsFilterFocused,
} from '../filtering_selectors';

export const STATIC_EMPTY_ARRAY = [];

interface OwnProps {
  config: ConfigObjectType;
  filterAndSearchStateId: StateId;
  filteringStateId: StateId;
  isClickable?: boolean;
  level?: number;
  node: FilterNodeModel;
  parentName?: string;
  schema: FilterSchema;
  searchServiceUrl: string;
}

const mapState = () => (state: RootState, props: OwnProps) => ({
  autocompleteOptions: state[props.filteringStateId].autocompleteOptions,
  autocompleteValues: state[props.filteringStateId].autocompleteValues,
  filtersData: state[props.filteringStateId].filtersData,
  filterFocused: makeIsFilterFocused()(state, props),
  openedFilter: makeGetOpenedFilter()(state, props),
  searchServiceUrl: getSearchServiceUrl(state, props),
  selectedAutocompleteValues: makeGetSelectedAutocompleteValues()(state, props),
  selectedFilter: makeGetSelectedFilter()(state, props),
  filterRouteParam: getRouterLocationQuery(state as any).filter,
});

const mapDispatch = {
  autocompleteAC: autocomplete,
  autocompleteGetLabelsAC: autocompleteGetLabels,
  autocompleteSetValuesAC: autocompleteSetValues,
  changeFilterAC: changeFilter,
  changeFilterAndQueryAC: changeFilterAndQuery,
  focusFilterAC: focusFilter,
  removeFilterAC: removeFilter,
  toggleOpenedAC: toggleOpened,
  removeFilterAndQueryAC: removeFilterAndQuery,
  changeParamsAndQueryAC: changeParamsAndQuery,
};

const connector = connect(mapState, mapDispatch);
type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = PropsFromRedux & OwnProps;

const MUIFilterNodeContainerComponent: React.FC<Props> = (props) => {
  const {
    config,
    filterFocused,
    filteringStateId,
    filterAndSearchStateId,
    isClickable = false,
    level = 0,
    node,
    openedFilter,
    parentName,
    searchServiceUrl,
    selectedAutocompleteValues,
    selectedFilter,
    schema,
    autocompleteAC,
    autocompleteGetLabelsAC,
    autocompleteSetValuesAC,
    changeFilterAndQueryAC,
    focusFilterAC,
    toggleOpenedAC,
    removeFilterAndQueryAC,
    changeParamsAndQueryAC,
    autocompleteOptions,
    autocompleteValues,
  } = props;

  const debouncedChangeParamsAndQuery = useMemo(
    () => debounce(changeParamsAndQueryAC, 500),
    [changeParamsAndQueryAC]
  );

  const toggleOpened = () => {
    toggleOpenedAC(filteringStateId, node.getName());
    if (!openedFilter) {
      focusFilterAC(filteringStateId, node.getId());
    }
  };

  const isFilterSelected = (filter: Filter) => {
    // TODO: was selectedFilters which does not exists in props / state
    return Filter.isFilterSelected(selectedFilter, filter);
  };

  const changeFilter = (filter: Filter) => {
    changeFilterAndQueryAC(
      searchServiceUrl,
      filter,
      debouncedChangeParamsAndQuery,
      filteringStateId,
      filterAndSearchStateId
    );
  };

  const removeFilter = (filter: Filter) => {
    removeFilterAndQueryAC(
      searchServiceUrl,
      filter,
      debouncedChangeParamsAndQuery,
      filteringStateId,
      filterAndSearchStateId
    );
  };

  const autocomplete = (input: any) => {
    const url = node.isSecured()
      ? config.service.url.securedHierarchiesByPrefixMethod
      : config.service.url.hierarchiesByPrefixMethod;
    const promise = autocompleteAC(
      filteringStateId,
      config.service.url.hierarchies,
      {
        type: node.getHierarchyType(),
        prefix: input,
      },
      url
    );
    return promise.then(() => {
      const options = autocompleteOptions[node.getHierarchyType()];
      return options ? options.getMappedOptions() : STATIC_EMPTY_ARRAY;
    });
  };

  const autocompleteGetLabels = (paths: any) => {
    const joinedPaths = (paths || []).join(',');
    const byPathsUrl = node.isSecured()
      ? config.service.url.securedHierarchiesByPathsMethod
      : config.service.url.hierarchiesByPathsMethod;
    const promise = autocompleteGetLabelsAC(
      filteringStateId,
      config.service.url.hierarchies,
      {
        type: node.getHierarchyType(),
        paths: joinedPaths,
      },
      byPathsUrl
    );
    return promise.then(() => {
      const options = autocompleteValues[node.getHierarchyType()];
      return {
        options: options ? options.getMappedOptions() : STATIC_EMPTY_ARRAY,
      };
    });
  };

  const autocompleteSetValues = (values: any) => {
    const autocompleteMethod =
      node.getAutocompleteMethod() || AutocompleteMethod.BY_LABEL;
    if (autocompleteMethod === AutocompleteMethod.BY_PATH) {
      autocompleteSetValuesAC(
        filteringStateId,
        node.getHierarchyType(),
        values
      );
    } else if (autocompleteMethod === AutocompleteMethod.BY_LABEL) {
      autocompleteGetLabels(values);
    }
  };

  const currentParentName =
    node.getType() === FILTER_TYPE_BOOL || node.getType() === FILTER_TYPE_RADIO
      ? node.getName()
      : parentName;
  const areChildrenClickable =
    isClickable ||
    node.getType() === FILTER_TYPE_BOOL ||
    node.getType() === FILTER_TYPE_RADIO;

  return (
    <MuiFilterNode
      config={config}
      filterFocused={filterFocused}
      filteringStateId={filteringStateId}
      filterAndSearchStateId={filterAndSearchStateId}
      isClickable={isClickable}
      isOpened={!!openedFilter}
      level={level}
      node={node}
      schema={schema}
      searchServiceUrl={searchServiceUrl}
      selectedAutocompleteValues={selectedAutocompleteValues}
      selectedFilter={selectedFilter}
      areChildrenClickable={areChildrenClickable}
      autocomplete={autocomplete}
      changeFilter={changeFilter}
      isFilterSelected={isFilterSelected}
      parentName={currentParentName}
      autocompleteSetValues={autocompleteSetValues}
      removeFilter={removeFilter}
      toggleOpened={toggleOpened}
    />
  );
};

MUIFilterNodeContainerComponent.displayName = 'MuiFilterNodeContainer';

export const MuiFilterNodeContainer = connector(
  MUIFilterNodeContainerComponent
);
